/* eslint-disable no-param-reassign */
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { ACTION_TYPES } from '../Context/AppContext';

const setTokens = (accessToken, refreshToken = '') => {
	localStorage.setItem('accessToken', accessToken);
	if (refreshToken !== '') {
		localStorage.setItem('refreshToken', refreshToken);
	}
};

export const ACTIVE_ORG_STORAGE_KEY = 'activeOrganization';

export const getStoredActiveOrganization = () => {
	const raw = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
};

export const refreshSuperuserOrganizationContext = async (organizationId) => {
	const organizations = await makeApiRequest('GET', '/auth/organizations/');
	return (organizations || []).find((org) => org.id === organizationId) ?? null;
};

export const storeActiveOrganization = (organization) => {
	if (!organization?.id) {
		return;
	}
	localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, JSON.stringify(organization));
};

export const clearStoredActiveOrganization = () => {
	localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
};

export const clearLocalAuth = () => {
	localStorage.removeItem('accessToken');
	localStorage.removeItem('refreshToken');
	clearStoredActiveOrganization();
};

export const applySuperuserOrganization = (userData) => {
	if (!userData?.is_superuser) {
		return userData;
	}
	const stored = getStoredActiveOrganization();
	if (stored?.id) {
		return { ...userData, organization: stored };
	}
	return userData;
};

const getTokens = () => {
	const accessTokenString = localStorage.getItem('accessToken');
	const refreshTokenString = localStorage.getItem('refreshToken');
	return { accessToken: accessTokenString, refreshToken: refreshTokenString };
};

export const getAccessToken = () => getTokens().accessToken;

class ApiError extends Error {
	constructor({ status, message, data }) {
		super();
		this.status = status;
		this.message = message;
		this.data = data;
	}
}

const forceLogout = () => {
	clearLocalAuth();
	window.location = '/login';
};

const AUTH_ENDPOINTS_SKIP_TOKEN_REFRESH = [
	'/auth/login/',
	'/auth/password-reset/',
	'/auth/password-reset/confirm/',
	'/auth/signup/',
];

const shouldSkipTokenRefresh = (request) => {
	const url = request?.url || '';
	return AUTH_ENDPOINTS_SKIP_TOKEN_REFRESH.some((path) => url.includes(path));
};

const apiClient = axios.create({
	baseURL: process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/',
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	},
	withCredentials: false,
});

// Request interceptor to add token to every request
apiClient.interceptors.request.use(
	(config) => {
		const tokens = getTokens();
		if (tokens.accessToken) {
			config.headers.Authorization = `Bearer ${tokens.accessToken}`;
		}
		const activeOrg = getStoredActiveOrganization();
		if (activeOrg?.id) {
			config.headers['X-Organization-Id'] = activeOrg.id;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

// Response interceptor to handle token refresh automatically
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

apiClient.interceptors.response.use(
	(response) => {
		return response;
	},
	async (error) => {
		const originalRequest = error.config;

		if (shouldSkipTokenRefresh(originalRequest)) {
			return Promise.reject(error);
		}

		// If error is 401 and we haven't tried to refresh yet
		if (error.response?.status === 401 && !originalRequest._retry) {
			if (isRefreshing) {
				// If already refreshing, queue this request
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				})
					.then((token) => {
						originalRequest.headers.Authorization = `Bearer ${token}`;
						return apiClient(originalRequest);
					})
					.catch((err) => {
						return Promise.reject(err);
					});
			}

			originalRequest._retry = true;
			isRefreshing = true;

			const tokens = getTokens();

			if (!tokens.refreshToken) {
				forceLogout();
				return Promise.reject(error);
			}

			try {
				const response = await axios.post(
					`${
						process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'
					}/auth/token/refresh/`,
					{ refresh: tokens.refreshToken },
				);

				const { access, refresh } = response.data;
				setTokens(access, refresh);

				apiClient.defaults.headers.common.Authorization = `Bearer ${access}`;
				originalRequest.headers.Authorization = `Bearer ${access}`;

				processQueue(null, access);

				return apiClient(originalRequest);
			} catch (refreshError) {
				processQueue(refreshError, null);
				forceLogout();
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		return Promise.reject(error);
	},
);

const sanitizeErrorMessage = (raw) => {
	if (!raw || typeof raw !== 'string') {
		return raw;
	}

	const trimmed = raw.trim();
	if (!trimmed) {
		return 'An error occurred';
	}

	if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
		const titleMatch = trimmed.match(/<title>([^<]+)<\/title>/i);
		if (titleMatch) {
			return titleMatch[1].replace(/\s+at\s+/, ': ').slice(0, 160);
		}
		return 'Something went wrong on the server. Please try again.';
	}

	const firstLine = trimmed.split('\n').find((line) => line.trim()) || trimmed;
	if (firstLine.length > 160) {
		return `${firstLine.slice(0, 157)}...`;
	}
	return firstLine;
};

const handleApiResponse = (error) => {
	if (error.response) {
		console.warn('API Error Response:', error.response);

		const { status, config, data: errorData } = error.response;
		if (status >= 500 && process.env.REACT_APP_SENTRY_DSN) {
			Sentry.withScope((scope) => {
				scope.setTag('api.status', status);
				if (config?.url) {
					scope.setTag('api.url', config.url);
				}
				if (config?.method) {
					scope.setTag('api.method', config.method.toUpperCase());
				}
				Sentry.captureException(error, {
					extra: { responseData: errorData },
				});
			});
		}

		let errorMessage = 'An error occurred';

		if (typeof errorData === 'string') {
			errorMessage = sanitizeErrorMessage(errorData);
		} else if (errorData.error) {
			errorMessage = sanitizeErrorMessage(
				typeof errorData.error === 'string'
					? errorData.error
					: String(errorData.error),
			);
		} else if (errorData.detail) {
			errorMessage = sanitizeErrorMessage(
				typeof errorData.detail === 'string'
					? errorData.detail
					: String(errorData.detail),
			);
		} else if (typeof errorData === 'object') {
			const firstError = Object.values(errorData)[0];
			const raw = Array.isArray(firstError) ? firstError[0] : firstError;
			errorMessage = sanitizeErrorMessage(
				typeof raw === 'string' ? raw : String(raw),
			);
		}

		throw new ApiError({
			status: error.response.status,
			message: errorMessage,
			data: errorData,
		});
	} else if (error.request) {
		console.warn('API Request Error:', error.request);
		throw new ApiError({
			status: 500,
			message: 'No response received! Please check your connection.',
			data: error.request,
		});
	} else {
		console.warn('API Error:', error.message);
		throw new ApiError({
			status: 500,
			message: 'An unknown error occurred.',
			data: {},
		});
	}
};

export const makeApiRequest = async (
	method,
	url,
	body = null,
	queryParams = {},
) => {
	try {
		const response = await apiClient.request({
			url,
			method,
			params: queryParams,
			data:
				['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && body
					? body
					: undefined,
		});
		return response.data;
	} catch (e) {
		handleApiResponse(e);
	}
};

export const makeMultipartApiRequest = async (
	method,
	url,
	formData,
	queryParams = {},
) => {
	try {
		const response = await apiClient.request({
			url,
			method,
			params: queryParams,
			data: formData,
			headers: {
				Accept: 'application/json',
			},
			transformRequest: [
				(data, headers) => {
					if (data instanceof FormData) {
						delete headers['Content-Type'];
					}
					return data;
				},
			],
		});
		return response.data;
	} catch (e) {
		handleApiResponse(e);
	}
};

// ** Login Management **

const applyAuthResponse = (authResponse, dispatch) => {
	setTokens(authResponse.access, authResponse.refresh);

	dispatch({
		type: ACTION_TYPES.LOGGED_IN,
		payload: {
			accessToken: authResponse.access,
			refreshToken: authResponse.refresh,
		},
	});

	if (authResponse.user) {
		let userData = authResponse.user;
		if (userData.is_superuser) {
			userData = applySuperuserOrganization(userData);
			if (userData.organization?.id) {
				storeActiveOrganization(userData.organization);
			}
		} else {
			clearStoredActiveOrganization();
		}
		dispatch({
			type: ACTION_TYPES.UPDATE_USER,
			payload: userData,
		});
	}
};

export const loginUser = async (data, dispatch) => {
	try {
		const loginResponse = await makeApiRequest('POST', '/auth/login/', data);
		applyAuthResponse(loginResponse, dispatch);
		return loginResponse;
	} catch (error) {
		console.error('Login failed:', error);
		throw error;
	}
};

export const logoutUser = async () => {
	const tokens = getTokens();
	try {
		await makeApiRequest('POST', '/auth/logout/', {
			refresh: tokens.refreshToken,
		});
	} finally {
		clearLocalAuth();
	}
};

export const signupUser = async (
	{ email, password, orgName, firstName = '', lastName = '', phone, acceptTerms },
	dispatch,
) => {
	const authResponse = await makeApiRequest('POST', '/auth/signup/', {
		email,
		password,
		org_name: orgName,
		first_name: firstName,
		last_name: lastName,
		phone,
		accept_terms: acceptTerms,
	});
	applyAuthResponse(authResponse, dispatch);
	return authResponse;
};

export const startConversionCheckout = (plan) =>
	makeApiRequest('POST', '/payments/billing/convert/', { plan });

export const completeConversionCheckout = (sessionId) =>
	makeApiRequest('POST', '/payments/billing/convert/complete/', {
		session_id: sessionId,
	});

export const deactivateTrialAccount = () =>
	makeApiRequest('POST', '/payments/billing/deactivate-trial/');

export const fetchOnboarding = () => makeApiRequest('GET', '/auth/onboarding/');

export const dismissOnboarding = () =>
	makeApiRequest('POST', '/auth/onboarding/dismiss/');

export const restoreOnboarding = () =>
	makeApiRequest('POST', '/auth/onboarding/restore/');

export const skipStripeOnboarding = () =>
	makeApiRequest('POST', '/auth/onboarding/skip-stripe/');

export const fetchProOnboarding = () =>
	makeApiRequest('GET', '/auth/pro-onboarding/');

export const dismissProOnboarding = () =>
	makeApiRequest('POST', '/auth/pro-onboarding/dismiss/');

export const restoreProOnboarding = () =>
	makeApiRequest('POST', '/auth/pro-onboarding/restore/');

export const skipProOnboardingStep = (step) =>
	makeApiRequest('POST', `/auth/pro-onboarding/skip/${step}/`);

export const markProOnboardingSeen = (step) =>
	makeApiRequest('POST', `/auth/pro-onboarding/seen/${step}/`);

export const sendOnboardingSampleBid = () =>
	makeApiRequest('POST', '/auth/my-organization/send-sample-bid/');

export const fetchSubscriptionSummary = () =>
	makeApiRequest('GET', '/payments/billing/subscription/');

export const openBillingPortal = () =>
	makeApiRequest('POST', '/payments/billing/portal/');

export const fetchUpgradePreview = () =>
	makeApiRequest('GET', '/payments/billing/upgrade-preview/');

export const upgradeToPro = () =>
	makeApiRequest('POST', '/payments/billing/upgrade/');

export const downgradeToBasic = (keepUserId) =>
	makeApiRequest('POST', '/payments/billing/downgrade/', {
		keep_user_id: keepUserId,
	});

export const cancelScheduledDowngrade = () =>
	makeApiRequest('POST', '/payments/billing/cancel-downgrade/');

export const cancelSubscription = () =>
	makeApiRequest('POST', '/payments/billing/cancel/');

export const reactivateSubscription = () =>
	makeApiRequest('POST', '/payments/billing/reactivate/');

export const fetchSeatSummary = () =>
	makeApiRequest('GET', '/auth/users/seat-summary/');

export const fetchSeatBillingPreview = (seatCount) =>
	makeApiRequest(
		'GET',
		`/payments/billing/seat-preview/?seat_count=${seatCount}`,
	);

export const fetchUpgradeSeatSelection = () =>
	makeApiRequest('GET', '/payments/billing/upgrade-seat-selection/');

export const confirmUpgradeSeatSelection = (selectedUserIds) =>
	makeApiRequest('POST', '/payments/billing/upgrade-seat-selection/', {
		selected_user_ids: selectedUserIds,
	});

export const fetchDowngradeSeatSelection = () =>
	makeApiRequest('GET', '/payments/billing/downgrade-seat-selection/');

export const confirmDowngradeSeatSelection = (keepUserId) =>
	makeApiRequest('POST', '/payments/billing/downgrade-seat-selection/', {
		keep_user_id: keepUserId,
	});

export const removeUser = (userId, confirmBilling = false) =>
	makeApiRequest('POST', `/auth/users/${userId}/remove/`, {
		confirm_billing: confirmBilling,
	});

export const fetchJobAssignments = (params = {}) =>
	makeApiRequest('GET', '/jobs/assignments/', null, params);

export const fetchScheduleBoard = (params = {}) =>
	makeApiRequest('GET', '/jobs/schedule/board/', null, params);

export const createJobAssignment = (data) =>
	makeApiRequest('POST', '/jobs/assignments/', data);

export const updateJobAssignment = (id, data) =>
	makeApiRequest('PATCH', `/jobs/assignments/${id}/`, data);

export const deleteJobAssignment = (id) =>
	makeApiRequest('DELETE', `/jobs/assignments/${id}/`);

export const fetchAssignableUsers = () =>
	makeApiRequest('GET', '/jobs/assignable-users/');

export const fetchTimeclockStatus = () =>
	makeApiRequest('GET', '/timeclock/status/');

export const clockIn = (data = {}) =>
	makeApiRequest('POST', '/timeclock/clock-in/', data);

export const clockOut = (data = {}) =>
	makeApiRequest('POST', '/timeclock/clock-out/', data);

export const forgottenClockOut = (data) =>
	makeApiRequest('POST', '/timeclock/forgotten-clock-out/', data);

export const fetchTimeEntries = (params = {}) =>
	makeApiRequest('GET', '/timeclock/entries/', null, params);

export const updateTimeEntry = (id, data) =>
	makeApiRequest('PATCH', `/timeclock/entries/${id}/`, data);

export const approveTimeEntry = (id, data = {}) =>
	makeApiRequest('POST', `/timeclock/entries/${id}/approve/`, data);

export const rejectTimeEntry = (id, data = {}) =>
	makeApiRequest('POST', `/timeclock/entries/${id}/reject/`, data);

export const fetchFailedClockInAttempts = () =>
	makeApiRequest('GET', '/timeclock/failed-attempts/');

export const fetchAnalyticsDashboard = (params = {}) =>
	makeApiRequest('GET', '/analytics/dashboard/', null, params);

export const fetchPlaceSuggestions = (data) =>
	makeApiRequest('POST', '/places/autocomplete/', data);

export const fetchPlaceDetails = (data) =>
	makeApiRequest('POST', '/places/details/', data);

export const geocodeAddress = (data) =>
	makeApiRequest('POST', '/places/geocode/', data);

export const updateCurrentUser = (data) =>
	makeApiRequest('PATCH', '/auth/me/', data);
